import type { ReactNode } from 'react';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/ui';
import { LocaleBridge } from './i18n/bridge';
import { SessionProvider } from './lib/session';

/**
 * El orden de los proveedores, en un sitio que se puede probar.
 *
 * No es decorativo, y el fallo que lo demuestra no se veía cargando ninguna
 * página: `ToastProvider` pinta cada aviso con `useT()`, así que estando por
 * fuera del proveedor de idioma, el primer aviso que apareciera —guardar un
 * cambio, suspender a un cliente, subir un archivo— lanzaba «useI18n debe
 * usarse dentro de <I18nProvider>» y dejaba **la pantalla en blanco**. Sólo se
 * manifestaba al acertar con una acción que saliera bien.
 *
 * La cadena es: sesión → idioma → avisos → **límite de error** → aplicación.
 * El idioma sale del perfil, que vive en la sesión; los avisos se traducen, así
 * que van dentro del idioma.
 *
 * El límite va el último, y no por comodidad: la pantalla que pinta cuando algo
 * revienta traduce con `useT()`, así que por fuera del idioma el propio
 * respaldo lanzaría y volveríamos a la pantalla en blanco que cuenta el párrafo
 * de arriba. Eso deja estos cuatro proveedores sin cubrir; es el precio, y lo
 * cubre el test de este archivo.
 *
 * Vive fuera de `main.tsx` para que un test pueda montar exactamente esta
 * composición y disparar un aviso, que es lo único que habría cazado aquello.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <SessionProvider>
      <LocaleBridge>
        <ToastProvider>
          <RouteErrorBoundary>{children}</RouteErrorBoundary>
        </ToastProvider>
      </LocaleBridge>
    </SessionProvider>
  );
}
